import { Client, Room, getStateCallbacks } from "colyseus.js";
import bs58 from "bs58";
import { LOBBY_PORT } from "@solblox/shared";
import type { MoveMsg } from "@solblox/shared";

import { useStore, type ChatMessageView, type PlayerView } from "../store";
import { trackEvent } from "../launch/useTrack";

/**
 * Wallet-sig challenge — must stay in lockstep with the server-side
 * `signedPlayerPubkeyPayload` in server/src/bridge/HostBridge.ts. If you
 * change one, change both.
 */
const PLAYER_PUBKEY_DOMAIN = "solblox-player-pubkey|v1";

async function signedPlayerPubkeyPayload(
  roomId: string,
  nonce: string,
): Promise<Uint8Array> {
  const text = `${PLAYER_PUBKEY_DOMAIN}|${roomId}|${nonce}`;
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return new Uint8Array(digest);
}

/**
 * Caller-supplied signer. Matches the shape of `wallet.signMessage` from
 * @solana/wallet-adapter-react. Returns the raw 64-byte ed25519 signature.
 *
 * `pubkey` is the base58 PublicKey the signer holds; we send both so the
 * server can verify without guessing.
 */
export interface PubkeyAttester {
  pubkey: string;
  signMessage: (message: Uint8Array) => Promise<Uint8Array>;
}

/**
 * Install the wallet-sig challenge response on a freshly joined room. The
 * server sends `pubkey:challenge { nonce }` immediately on join; we reply
 * with `pubkey:attest { pubkey, signature }` after the wallet signs.
 *
 * If `attester` is undefined (guest, no wallet connected) we skip entirely —
 * `player.pubkey` simply stays empty on the schema.
 */
function installPubkeyAttest(room: Room, attester: PubkeyAttester | undefined): void {
  // Always register a handler — the server sends `pubkey:challenge` to every
  // client on join, including guests; without a handler Colyseus logs a
  // benign "onMessage() not registered" warning. Guests no-op the response.
  room.onMessage("pubkey:challenge", async (msg: { nonce?: unknown }) => {
    if (!attester) return;
    const nonce = typeof msg?.nonce === "string" ? msg.nonce : "";
    if (nonce.length === 0) return;
    try {
      const payload = await signedPlayerPubkeyPayload(room.roomId, nonce);
      const sig = await attester.signMessage(payload);
      room.send("pubkey:attest", {
        pubkey: attester.pubkey,
        signature: bs58.encode(sig),
      });
    } catch (err) {
      // User can reject the sign prompt or the wallet may be locked — that's
      // fine, player.pubkey just stays empty until they reconnect and accept.
      // eslint-disable-next-line no-console
      console.warn("[colyseus] pubkey attest failed", err);
    }
  });
}

/**
 * Resolve the Colyseus endpoint. Prefer VITE_COLYSEUS_URL when set; otherwise
 * connect to the local lobby server (Lane B).
 */
function endpoint(): string {
  const env = import.meta.env.VITE_COLYSEUS_URL as string | undefined;
  if (env && env.length > 0) return env;
  // Fall back to ws://<page-host>:LOBBY_PORT — works in dev and same-network setups.
  const host =
    typeof window !== "undefined" ? window.location.hostname : "127.0.0.1";
  return `ws://${host}:${LOBBY_PORT}`;
}

let _client: Client | null = null;
function getClient(): Client {
  if (!_client) _client = new Client(endpoint());
  return _client;
}

export interface JoinLobbyOpts {
  displayName: string;
  avatarUrl: string;
  /**
   * Wallet-adapter signer for the pubkey-attest challenge. Omit for guests —
   * the lobby will accept them, `player.pubkey` just stays empty.
   */
  attester?: PubkeyAttester;
  /** Join as a listen-only spectator (no playable avatar). */
  asSpectator?: boolean;
  /** Optional pre-picked skin color (must be in PLAYER_COLOR_PALETTE). */
  colorHex?: string;
}

export interface JoinArenaOpts {
  /** Deterministic match seed; server may regenerate if absent. */
  seed?: string;
  /** Optional pre-allocated session id (host-led flow). */
  sessionId?: string;
  /**
   * Wallet-adapter signer for the pubkey-attest challenge. Omit for guests —
   * the arena will accept them, `player.pubkey` just stays empty.
   */
  attester?: PubkeyAttester;
  /** Join as a listen-only spectator (no spawn, not counted in cap). */
  asSpectator?: boolean;
}

/** Convert a Player schema record to a plain PlayerView. */
function viewFromPlayer(id: string, p: PlayerRecord): PlayerView {
  return {
    id,
    displayName: p.displayName ?? "",
    x: p.x ?? 0,
    y: p.y ?? 0,
    z: p.z ?? 0,
    rotY: p.rotY ?? 0,
    avatarUrl: p.avatarUrl ?? "",
    pubkey: p.pubkey ?? "",
    isSpectator: p.isSpectator === true,
    colorHex: p.colorHex ?? "",
    isHolder: p.isHolder === true,
  };
}

/**
 * Minimal structural shape we read off Colyseus Player schemas. We avoid
 * importing the actual Schema class at the type boundary so this file does
 * not require @colyseus/schema at build time.
 */
interface PlayerRecord {
  displayName: string;
  x: number;
  y: number;
  z: number;
  rotY: number;
  avatarUrl: string;
  pubkey?: string;
  isSpectator?: boolean;
  colorHex?: string;
  isHolder?: boolean;
  onChange?: (cb: () => void) => void;
}

interface ChatRecord {
  id: string;
  from: string;
  body: string;
  ts: number;
  holder?: boolean;
}

/**
 * Loose shape of one node in the schema-callback proxy tree. The colyseus.js
 * public types only re-export the post-application shape (`SchemaCallback<T>`),
 * which is not callable; the runtime root is a function `<T>(instance) => Proxy<T>`
 * and each navigated property is itself callable with the same shape. We use a
 * single `Cb` shape that is wide enough for both collection and per-instance use.
 */
interface CbNode {
  onAdd: (cb: (item: PlayerRecord & ChatRecord, key: string) => void) => () => void;
  onRemove: (cb: (item: PlayerRecord, key: string) => void) => () => void;
  onChange: (cb: () => void) => () => void;
  players: CbNode;
  chat: CbNode;
}
type CbFactory = (instance: unknown) => CbNode;

/**
 * Wire a freshly joined lobby room into the zustand store. Uses the
 * `getStateCallbacks(room)` proxy API introduced in @colyseus/schema v3 —
 * direct `state.players.onAdd(...)` no longer registers a callback in v3+,
 * the callbacks live on the decoder-side proxy returned by getStateCallbacks.
 */
function wireLobbyRoom(room: Room, selfId: string): void {
  const $ = getStateCallbacks(room) as unknown as CbFactory;
  const state = room.state as unknown;
  const $state = $(state);

  $state.players.onAdd((player, key) => {
    const view = viewFromPlayer(key, player);
    if (key === selfId) {
      useStore.getState().setLocalPlayer(view);
    } else {
      useStore.getState().upsertRemotePlayer(view);
    }
    // Per-instance onChange fires on any property of this Player schema.
    $(player).onChange(() => {
      const updated = viewFromPlayer(key, player);
      if (key === selfId) useStore.getState().setLocalPlayer(updated);
      else useStore.getState().upsertRemotePlayer(updated);
    });
  });

  $state.players.onRemove((_player, key) => {
    if (key === selfId) {
      useStore.getState().setLocalPlayer(null);
    } else {
      useStore.getState().removeRemotePlayer(key);
    }
  });

  $state.chat.onAdd((msg) => {
    const view: ChatMessageView = {
      id: msg.id,
      from: msg.from,
      body: msg.body,
      ts: msg.ts,
      holder: msg.holder === true,
    };
    useStore.getState().appendChat(view);
  });

  // Chat write-gate flag (set at room creation, immutable for the room's life):
  // surface it so the chat input matches the server's enforcement.
  useStore
    .getState()
    .setChatGated((state as { chatGated?: boolean }).chatGated === true);

  room.onLeave(() => {
    // Only clear store state if THIS room is still the current one. Under React
    // StrictMode the first mount's room is left asynchronously after the second
    // mount has already joined a new room and registered its remotes; without
    // this guard the stale leave wipes the live room's player list.
    const store = useStore.getState();
    if (store.currentRoom === room) {
      store.setCurrentRoom(null);
      store.clearRemotePlayers();
    }
  });
  room.onError((code, message) => {
    // eslint-disable-next-line no-console
    console.error("[colyseus] room error", code, message);
  });
}

/** Join (or create) the singleton lobby room. */
export async function joinLobby(opts: JoinLobbyOpts): Promise<Room> {
  const client = getClient();
  const room = await client.joinOrCreate("lobby", {
    displayName: opts.displayName,
    avatarUrl: opts.avatarUrl,
    asSpectator: opts.asSpectator === true,
    colorHex: opts.colorHex ?? "",
  });
  installPubkeyAttest(room, opts.attester);
  useStore.getState().setCurrentRoom(room);
  wireLobbyRoom(room, room.sessionId);
  trackEvent("room_enter", { wallet: opts.attester?.pubkey ?? null });
  return room;
}

/** Join (or create) a Coin Arena match. Lane F owns the room internals. */
export async function joinArena(opts: JoinArenaOpts = {}): Promise<Room> {
  const client = getClient();
  const room = await client.joinOrCreate("arena", {
    seed: opts.seed ?? "",
    sessionId: opts.sessionId ?? "",
    asSpectator: opts.asSpectator === true,
  });
  installPubkeyAttest(room, opts.attester);
  useStore.getState().setCurrentRoom(room);
  // Reuse the lobby player-wiring for the arena's MapSchema<ArenaPlayer> —
  // ArenaPlayer extends Player, so all the fields we read are present.
  wireLobbyRoom(room, room.sessionId);
  trackEvent("room_enter", { wallet: opts.attester?.pubkey ?? null });
  return room;
}

/** Send a movement intent to whatever room we are currently in. */
export function sendMove(msg: MoveMsg): void {
  const room = useStore.getState().currentRoom;
  if (!room) return;
  room.send("move", msg);
}

/** Tell the server to update this client's avatar color. Server validates. */
export function sendColorSet(colorHex: string): void {
  const room = useStore.getState().currentRoom;
  if (!room) return;
  room.send("color:set", { colorHex });
}

/**
 * Request a custom display name. Server accepts only from verified token
 * holders (and sanitizes/clamps), so non-holders are a silent no-op.
 */
export function sendNameSet(name: string): void {
  const room = useStore.getState().currentRoom;
  if (!room) return;
  room.send("name:set", { name });
}

/** Leave the current room, if any. */
export async function leaveCurrentRoom(): Promise<void> {
  const room = useStore.getState().currentRoom;
  if (!room) return;
  await room.leave(true);
  useStore.getState().setCurrentRoom(null);
  useStore.getState().setLocalPlayer(null);
  useStore.getState().clearRemotePlayers();
}
